// src/store/slices/leaveSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import leaveApi from 'api/leaveApi';
import { toast } from 'sonner';

// ────────────────────────────────────────────────
// Fetch all leave requests (paginated)
export const fetchLeaves = createAsyncThunk(
  'leave/fetchLeaves',
  async ({ page = 1, pageSize = 10, search = '' }, { rejectWithValue }) => {
    try {
      const response = await leaveApi.getAll(page, pageSize, search);
      if (!response.success) {
        throw new Error(response.errors?.[0] || 'API returned unsuccessful');
      }
      return response;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.errors?.[0] ||
        err.message ||
        'Failed to load leave requests'
      );
    }
  }
);

// ────────────────────────────────────────────────
// Create new leave request
export const createLeaveRequest = createAsyncThunk(
  'leave/createLeaveRequest',
  async (data, { rejectWithValue }) => {
    try {
      // Just return the created object directly
      const createdLeave = await leaveApi.create(data);
      return createdLeave;
    } catch (err) {
      // console.log("111", err.response);
      // Try to extract meaningful message from backend
      const message =
        err.response?.data?.error||
        err.response?.data?.non_field_errors?.[0] ||
        err.response?.data?.detail ||
        err.response?.data?.leave_from?.[0] ||  // field-specific
        err.message ||
        'Failed to submit leave request';

      toast.error(message);

      return rejectWithValue(message);
    }
  }
);

// ────────────────────────────────────────────────
// Update existing leave request (PATCH)
export const updateLeaveRequest = createAsyncThunk(
  'leave/updateLeaveRequest',
  async ({ id, ...updates }, { rejectWithValue }) => {
    try {
      // Return the updated object directly
      const updatedLeave = await leaveApi.update(id, updates);
      return updatedLeave;
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.non_field_errors?.[0] ||
        err.response?.data?.detail ||
        err.message ||
        'Failed to update leave request';
 toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// ────────────────────────────────────────────────
// Approve leave
export const approveLeave = createAsyncThunk(
  'leave/approveLeave',
  async ({ id, manager_remarks  = '' }, { rejectWithValue }) => {
    try {
      const updated = await leaveApi.approve(id, manager_remarks );
      return updated;
    } catch (err) {
      const message =   err.response?.data?.detail ||
        err.response?.data?.error ||
        err.message ||
        'Failed to approve leave'
       toast.error(message);
      return rejectWithValue(
      message
      );
    }
  }
);
// ────────────────────────────────────────────────
// Reject leave
export const rejectLeave = createAsyncThunk(
  'leave/rejectLeave',
  async ({ id, manager_remarks  }, { rejectWithValue }) => {
    try {
      const updated = await leaveApi.reject(id, manager_remarks );
      return updated;
    } catch (err) {
      const message =    err.response?.data?.detail ||
        err.response?.data?.error ||
        err.message ||
        'Failed to reject leave'

       toast.error(message);
      return rejectWithValue(
        message
      );
    }
  }
);

// ────────────────────────────────────────────────
// Cancel leave
export const cancelLeave = createAsyncThunk(
  'leave/cancelLeave',
  async ({ id, remarks = '' }, { rejectWithValue }) => {
    try {
      const updated = await leaveApi.cancel(id, remarks);
      return updated;
    } catch (err) {
      const message =   err.response?.data?.detail ||
        err.response?.data?.error ||
        err.message ||
        'Failed to cancel leave'
        toast.error(message);
      return rejectWithValue(
      message
      );
    }
  }
);


// ────────────────────────────────────────────────
// Delete leave request
export const deleteLeave = createAsyncThunk(
  'leave/deleteLeave',
  async (id, { rejectWithValue }) => {
    try {
      await leaveApi.delete(id);
      return id; // return id for removal from state
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to delete leave request');
    }
  }
);


// ────────────────────────────────────────────────
// Export leaves to CSV
export const exportLeaves = createAsyncThunk(
  'leave/exportLeaves',
  async (_, { rejectWithValue }) => {
    try {
      const blob = await leaveApi.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leave-requests-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail ||
        err.message ||
        'Failed to export leave requests'
      );
    }
  }
);

export const fetchLeaveMeta = createAsyncThunk(
  'leave/fetchLeaveMeta',
  async (_, { rejectWithValue }) => {
    try {
      const response = await leaveApi.getLeaveMeta();
      return response.actions.POST.leave_type.choices;
    } catch (err) {
      return rejectWithValue(
        err.message || 'Failed to load leave types'
      );
    }
  }
);

// ────────────────────────────────────────────────
// Slice definition
const leaveSlice = createSlice({
  name: 'leave',
  initialState: {
    leaves: [],
    leaveTypes: [], 
    pagination: {
      count: 0,
      current_page: 1,
      total_pages: 1,
      page_size: 10,
    },
    // Current list view (set on fetchLeaves) — used to scope socket upserts.
    query: { page: 1, pageSize: 10, search: '' },
    leaveBalances: null, // not present in your current API response
    isLoading: false,
    error: null,
  },

  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    // ── Real-time: set full list (initial connect) ───────
    setLeaves: (state, action) => {
      const { leaves = [] } = action.payload;
      state.leaves = leaves;
      // state.pagination.count = leaves.length;  // only if you don't use real pagination
    },

    // ── Real-time: refresh rows already in the current view ─────
    // Used for the socket's `initial_leaves` snapshot (server's 10 newest,
    // unfiltered): it must never replace the paged / searched list, so only
    // rows that are already visible get their fields refreshed.
    mergeLeavesInView: (state, action) => {
      const incoming = Array.isArray(action.payload) ? action.payload : [];
      incoming.forEach((leave) => {
        if (!leave?.id) return;
        const index = state.leaves.findIndex((l) => l.id === leave.id);
        if (index !== -1) {
          state.leaves[index] = { ...state.leaves[index], ...leave };
        }
      });
    },

    // ── Real-time: add new or update existing leave ──────
    // Rows already in the current view are updated in place. A row that is not
    // in view is only inserted when it is a newly created leave AND the view is
    // page 1 with no search (otherwise it may not belong to this page/search;
    // the next REST fetch picks it up).
    addOrUpdateLeaveRealtime: {
      reducer: (state, action) => {
        const leave = action.payload;
        if (!leave?.id) return;

        const index = state.leaves.findIndex((l) => l.id === leave.id);

        if (index !== -1) {
          state.leaves[index] = {
            ...state.leaves[index],
            ...leave,
          };
          return;
        }

        const event = action.meta?.event;
        const { page = 1, pageSize, search = '' } = state.query || {};
        const isNew = event === undefined || event === 'create' || event === 'created';
        if (!isNew || page !== 1 || (search && String(search).trim())) return;

        state.leaves.unshift(leave);
        state.pagination.count += 1;
        const limit = pageSize || state.pagination.page_size;
        if (limit && state.leaves.length > limit) {
          state.leaves.length = limit;
        }
      },
      prepare: (leave, event) => ({ payload: leave, meta: { event } }),
    },

    // ── Real-time: remove deleted leave ──────────────────
    removeLeaveFromList: (state, action) => {
      const id = action.payload;
      const before = state.leaves.length;
      state.leaves = state.leaves.filter((l) => l.id !== id);
      if (state.leaves.length !== before) {
        state.pagination.count = Math.max(0, state.pagination.count - 1);
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // ── FETCH ────────────────────────────────────────
      .addCase(fetchLeaves.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        // Remember the current view so socket upserts can respect it.
        const { page = 1, pageSize = 10, search = '' } = action.meta?.arg || {};
        state.query = { page, pageSize, search };
      })
      .addCase(fetchLeaves.fulfilled, (state, action) => {
        state.isLoading = false;
        state.leaves = action.payload.data || [];
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchLeaves.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ── CREATE ───────────────────────────────────────
      .addCase(createLeaveRequest.fulfilled, (state, action) => {
        state.leaves.unshift(action.payload);
        state.pagination.count += 1;
      })

      // ── UPDATE ───────────────────────────────────────
      .addCase(updateLeaveRequest.fulfilled, (state, action) => {
        const index = state.leaves.findIndex((l) => l.id === action.payload.id);
        if (index !== -1) {
          state.leaves[index] = action.payload;
        }
      })

      // ── DELETE ───────────────────────────────────────
      .addCase(deleteLeave.fulfilled, (state, action) => {
        state.leaves = state.leaves.filter((l) => l.id !== action.payload);
        state.pagination.count = Math.max(0, state.pagination.count - 1);
      })

          // ── EXPORT (only error handling needed) ─────────
      .addCase(exportLeaves.rejected, (state, action) => {
        state.error = action.payload;
      })

            // leave type
      .addCase(fetchLeaveMeta.fulfilled, (state, action) => {
  state.leaveTypes = action.payload;
})
      // ── APPROVE / REJECT / CANCEL ────────────────────
      .addMatcher(
        (action) =>
          action.type.endsWith('/fulfilled') &&
          [
            'leave/approveLeave',
            'leave/rejectLeave',
            'leave/cancelLeave',
          ].includes(action.type),
        (state, action) => {
          const index = state.leaves.findIndex((l) => l.id === action.payload.id);
          if (index !== -1) {
            state.leaves[index] = {
              ...state.leaves[index],
              ...action.payload,
            };
          }
        }
      );



  
  },
});

export const {
  clearError,
  setLeaves,
  mergeLeavesInView,
  addOrUpdateLeaveRealtime,
  removeLeaveFromList,
} = leaveSlice.actions;

export default leaveSlice.reducer;